import React, { useState } from 'react';
import { Input, Button, Modal, Spin, Select, DatePicker } from 'antd';
import { useHolidayForm } from './hooks/useHolidayForm';
import dayjs from 'dayjs';

const { TextArea } = Input;

const HOLIDAY_TYPE_OPTIONS = [
  { value: 1, label: '1-國定假日' },
  { value: 2, label: '2-輪休' },
  { value: 3, label: '3-特別假' },
  { value: 4, label: '4-非正常班' },
  { value: 5, label: '5-全天班' },
  { value: 6, label: '6-半天班' },
];

const P910HForm: React.FC = () => {
  const {
    mode, isEditing, loading,
    records, current, currentIdx,
    form, setField,
    navFirst, navPrev, navNext, navLast,
    startAdd, startEdit, cancel, save,
    doDelete, quickSearch,
    formatDate, getDayOfWeek,
    HOLIDAY_TYPES,
  } = useHolidayForm();

  const [searchText, setSearchText] = useState('');

  const handleDelete = () => {
    if (!current) return;
    Modal.confirm({
      title: '確認刪除',
      content: `確定要刪除 ${formatDate(current.f90h_01)} 「${current.f90h_11}」？`,
      okText: '刪除',
      okType: 'danger',
      cancelText: '取消',
      onOk: doDelete,
    });
  };

  const handleQuickSearch = () => {
    if (searchText.trim()) {
      quickSearch(searchText.trim());
    }
  };

  const modeLabel = mode === 'add' ? '新增' : mode === 'edit' ? '修改' : '瀏覽';

  return (
    <Spin spinning={loading}>
      <div className="max-w-3xl mx-auto p-6">
        {/* Title */}
        <h2 className="text-xl font-bold mb-4">
          假日定義 (pw910hf1)
          <span className="ml-3 text-sm font-normal text-gray-500">
            [{modeLabel}] {records.length > 0 ? `${currentIdx + 1} / ${records.length}` : '無資料'}
          </span>
        </h2>

        {/* Quick Search Bar */}
        <div className="flex items-center gap-2 mb-4">
          <Input
            placeholder="輸入日期或名稱快速查詢"
            value={searchText}
            onChange={e => setSearchText(e.target.value)}
            onPressEnter={handleQuickSearch}
            disabled={isEditing}
            style={{ width: 300 }}
          />
          <Button onClick={handleQuickSearch} disabled={isEditing}>查詢</Button>
        </div>

        {/* Navigation Bar */}
        <div className="flex items-center gap-1 mb-4">
          <Button size="small" onClick={navFirst} disabled={isEditing || records.length === 0}>|&lt;</Button>
          <Button size="small" onClick={navPrev} disabled={isEditing || currentIdx <= 0}>&lt;</Button>
          <Button size="small" onClick={navNext} disabled={isEditing || currentIdx >= records.length - 1}>&gt;</Button>
          <Button size="small" onClick={navLast} disabled={isEditing || records.length === 0}>&gt;|</Button>
        </div>

        {/* Form Fields */}
        <div className="space-y-4 mb-6">
          {/* Date + Day of Week */}
          <div className="flex items-center gap-4">
            <div className="flex-1">
              <label className="block text-sm font-medium text-gray-700 mb-1">日期 (F90H_01)</label>
              {mode === 'add' ? (
                <DatePicker
                  value={form.f90h_01 ? dayjs(form.f90h_01) : null}
                  onChange={(date) => setField('f90h_01', date ? date.format('YYYY-MM-DD') : '')}
                  style={{ width: 200 }}
                  format="YYYY-MM-DD"
                />
              ) : (
                <Input
                  value={form.f90h_01}
                  disabled
                  style={{ width: 200 }}
                />
              )}
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">星期</label>
              <span className="text-base font-medium text-blue-600">
                {form.f90h_01 ? getDayOfWeek(form.f90h_01) : '-'}
              </span>
            </div>
          </div>

          {/* Holiday Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">日期名稱 (F90H_11)</label>
            <Input
              value={form.f90h_11}
              onChange={e => setField('f90h_11', e.target.value)}
              maxLength={12}
              disabled={!isEditing}
              style={{ width: 300 }}
              placeholder="如：國慶日、元旦、雙週休"
            />
          </div>

          {/* Holiday Type */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">假日別 (F90H_12)</label>
            <Select
              value={form.f90h_12}
              onChange={v => setField('f90h_12', v)}
              options={HOLIDAY_TYPE_OPTIONS}
              disabled={!isEditing}
              style={{ width: 200 }}
            />
            {!isEditing && current && (
              <span className="ml-2 text-gray-500">
                {HOLIDAY_TYPES[current.f90h_12] || ''}
              </span>
            )}
          </div>

          {/* Rotation Group */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">輪休組別 (F90H_13)</label>
            <Input
              value={form.f90h_13}
              onChange={e => setField('f90h_13', e.target.value)}
              maxLength={10}
              disabled={!isEditing}
              style={{ width: 200 }}
            />
          </div>

          {/* Work Schedule (for non-normal shifts) */}
          <div className="border rounded p-3 bg-gray-50">
            <p className="text-xs text-gray-500 mb-2">
              以下欄位用在非正常班(假日別=4)時設定，空白時依正常班之規定時間
            </p>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">上班時間 (F90H_21)</label>
                <Input
                  value={form.f90h_21}
                  onChange={e => setField('f90h_21', e.target.value)}
                  maxLength={5}
                  disabled={!isEditing}
                  style={{ width: 120 }}
                  placeholder="08:00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">下班時間 (F90H_22)</label>
                <Input
                  value={form.f90h_22}
                  onChange={e => setField('f90h_22', e.target.value)}
                  maxLength={5}
                  disabled={!isEditing}
                  style={{ width: 120 }}
                  placeholder="17:00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">午休開始 (F90H_23)</label>
                <Input
                  value={form.f90h_23}
                  onChange={e => setField('f90h_23', e.target.value)}
                  maxLength={5}
                  disabled={!isEditing}
                  style={{ width: 120 }}
                  placeholder="12:00"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">午休結束 (F90H_24)</label>
                <Input
                  value={form.f90h_24}
                  onChange={e => setField('f90h_24', e.target.value)}
                  maxLength={5}
                  disabled={!isEditing}
                  style={{ width: 120 }}
                  placeholder="13:00"
                />
              </div>
            </div>
          </div>

          {/* Remark */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">備註 (F90H_14)</label>
            <Input
              value={form.f90h_14}
              onChange={e => setField('f90h_14', e.target.value)}
              maxLength={60}
              disabled={!isEditing}
              style={{ width: '100%' }}
            />
          </div>

          {/* Summary */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">摘要 (F90H_15)</label>
            <TextArea
              value={form.f90h_15}
              onChange={e => setField('f90h_15', e.target.value)}
              disabled={!isEditing}
              rows={3}
              style={{ width: '100%' }}
            />
          </div>
        </div>

        {/* Audit Fields (read-only) */}
        {current && mode === 'browse' && (
          <div className="text-xs text-gray-400 mb-6 space-y-1">
            <div>建立人員: {current.f90h_91} | 建立日期: {current.f90h_92 ? formatDate(current.f90h_92) : '-'}</div>
            <div>修改人員: {current.f90h_93}</div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="flex items-center gap-2">
          {!isEditing ? (
            <>
              <Button type="primary" onClick={startAdd}>新增</Button>
              <Button onClick={startEdit} disabled={!current}>修改</Button>
              <Button danger onClick={handleDelete} disabled={!current}>刪除</Button>
            </>
          ) : (
            <>
              <Button type="primary" onClick={save}>儲存</Button>
              <Button onClick={cancel}>取消</Button>
            </>
          )}
        </div>
      </div>
    </Spin>
  );
};

export default P910HForm;
